// UserSelect.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { Select, Spin } from 'antd';
import _debounce from 'lodash/debounce';

import { useUserList } from 'api/users';

export const UserSelect = ({ onChange, value, placeholder, ...props }) => {
  const [searchString, setSearchString] = useState<string>('');
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState<number>(1);
  const [selectValue, setSelectValue] = useState<string | number | null>(value);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const size = 25;
  const { data, meta, isLoading, isValidating } = useUserList({
    page,
    size,
    search_string: searchString
  });
  const loading = isLoading || isValidating;

  useEffect(() => {
    setSelectValue(value ?? null);
  }, [value]);

  /* Debounced function */
  const onSearch = useCallback(
    _debounce(async (val: string) => {
      setSearchString(val);
      setPage(1);
      setUsers([]);
      setHasMore(true);
    }, 500),
    []
  );

  /* infinite scrolling */
  const handleScroll = e => {
    const { scrollTop, offsetHeight, scrollHeight } = e.target;

    if (hasMore && !loading && scrollTop + offsetHeight >= scrollHeight) {
      setPage(prevPage => prevPage + 1);
    }
  };

  /* handle user selection */
  const handleSelect = selectedUserId => {
    onChange(!selectedUserId ? null : selectedUserId);
    setSearchString('');
    setPage(1);
    setUsers([]);
    setHasMore(true);
  };

  /* clearing the input */
  const handleClear = () => {
    if (selectValue) {
      onChange(null);
      setSelectValue(null);
      setSearchString('');
      setPage(1);
      setUsers([]);
      setHasMore(true);
    }
  };

  /* update users list from SWR */
  useEffect(() => {
    if (!data) {
      return;
    }

    setUsers(prevUsers => (page === 1 ? data : [...prevUsers, ...data]));
    if (meta?.total_pages) {
      setHasMore(page < meta.total_pages);
    } else if (data.length < size) {
      setHasMore(false);
    }
  }, [data, meta?.total_pages, page, size]);

  return (
    <Select
      showSearch
      value={selectValue}
      loading={loading}
      placeholder={loading ? 'Loading...' : placeholder}
      style={{ width: '100%' }}
      onSearch={onSearch}
      onPopupScroll={handleScroll}
      filterOption={false}
      notFoundContent={loading ? <Spin size="small" /> : null}
      onSelect={handleSelect}
      onClear={handleClear}
      allowClear
      {...props}
    >
      {users.map(user => (
        <Select.Option key={user.id} value={user.id}>
          {user.email}
        </Select.Option>
      ))}
    </Select>
  );
};
